import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import { Modal } from "react-bootstrap";
import "../Console.css";
import "leaflet/dist/leaflet.css";
import { CircleMarker, MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";

const NAVBAR_H = 94;
const SIDEBAR_W = 360;
const UGANDA_BOUNDS = L.latLngBounds([-1.5, 29.3], [4.5, 35.1]);

const statusColor = (status) => {
  if (status === "Resolved") return "#22c55e";
  if (status === "Ongoing action") return "#eab308";
  return "#ef4444";
};

const statusChip = (status) => {
  if (status === "Resolved")
    return { bg: "rgba(34,197,94,0.12)", text: "#166534", label: "Resolved" };
  if (status === "Ongoing action")
    return { bg: "rgba(234,179,8,0.12)", text: "#713f12", label: "Ongoing action" };
  return { bg: "rgba(239,68,68,0.12)", text: "#7f1d1d", label: "Pending action" };
};

function FitToData({ trigger, points }) {
  const map = useMap();
  useEffect(() => {
    if (!trigger) return;
    setTimeout(() => {
      map.invalidateSize();
      const valid = points.filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
      if (!valid.length) {
        map.fitBounds(UGANDA_BOUNDS, { maxZoom: 9, animate: true });
        return;
      }
      if (valid.length === 1) {
        map.flyTo(valid[0], 15, { duration: 0.6 });
        return;
      }
      map.flyToBounds(L.latLngBounds(valid), {
        padding: [40, 40],
        maxZoom: 14,
        duration: 0.6,
      });
    }, 0);
  }, [trigger, points, map]);
  return null;
}

function FlyToPost({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 15), { duration: 0.5 });
  }, [target, map]);
  return null;
}

function formatDate(dt) {
  return new Date(dt).toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

const BleepMap = ({ domain }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [fitTrigger, setFitTrigger] = useState(0);
  const [flyTarget, setFlyTarget] = useState(null);
  const [coordsCopied, setCoordsCopied] = useState(false);
  const [idCopied, setIdCopied] = useState(false);
  const sidebarRef = useRef(null);

  const copyId = (id) => {
    navigator.clipboard.writeText(id).then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    });
  };

  const copyCoords = (lat, lng) => {
    navigator.clipboard.writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`).then(() => {
      setCoordsCopied(true);
      setTimeout(() => setCoordsCopied(false), 2000);
    });
  };

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setErr(null);
    fetch("/api/posts/mapdata", { signal: ctrl.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`API error ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setPosts(Array.isArray(data) ? data : []);
        setFitTrigger((k) => k + 1);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setErr(e.message || "Failed to load posts.");
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(posts.map((p) => p.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [posts]);

  const filteredPosts = useMemo(
    () =>
      posts.filter((p) => {
        if (filterStatus !== "all" && p.status !== filterStatus) return false;
        if (filterCategory !== "all" && p.category !== filterCategory) return false;
        return true;
      }),
    [posts, filterStatus, filterCategory]
  );

  const mapPoints = useMemo(
    () => filteredPosts.map((p) => [p.location.coordinates[1], p.location.coordinates[0]]),
    [filteredPosts]
  );

  const handleSelect = (post) => {
    setSelectedPost(post);
    setCoordsCopied(false);
    setIdCopied(false);
    setFlyTarget({ lat: post.location.coordinates[1], lng: post.location.coordinates[0], _t: Date.now() });
    const el = document.getElementById(`bleep-card-${post._id}`);
    if (el && sidebarRef.current) {
      sidebarRef.current.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Bleep Map | BLEEP</title>
        <meta name="description" content="View geotagged bleep reports on a map." />
      </Helmet>

      <div className="d-flex" style={{ height: `calc(100vh - ${NAVBAR_H}px)` }}>
        <Sidenav domain={domain} />

        <div
          style={{
            flex: 1,
            height: `calc(100vh - ${NAVBAR_H}px)`,
            display: "flex",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* ── Map ── */}
          <div style={{ position: "relative", flex: 1, height: `calc(100vh - ${NAVBAR_H}px)` }}>
            <MapContainer
              center={[0.3476, 32.5825]}
              zoom={7}
              scrollWheelZoom
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <FitToData trigger={fitTrigger} points={mapPoints} />
              <FlyToPost target={flyTarget} />

              {filteredPosts.map((post) => {
                const lat = post.location.coordinates[1];
                const lng = post.location.coordinates[0];
                const color = statusColor(post.status);
                const isSelected = selectedPost?._id === post._id;
                return (
                  <CircleMarker
                    key={post._id}
                    center={[lat, lng]}
                    pathOptions={{
                      color: isSelected ? "#fff" : color,
                      fillColor: color,
                      fillOpacity: isSelected ? 1 : 0.82,
                      weight: isSelected ? 3 : 1.5,
                    }}
                    radius={isSelected ? 11 : 8}
                    eventHandlers={{ click: () => handleSelect(post) }}
                  />
                );
              })}
            </MapContainer>

            {/* Loading overlay */}
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "40%",
                  zIndex: 1200,
                  background: "rgba(255,255,255,0.92)",
                  padding: "6px 14px",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                }}
              >
                <i className="fas fa-spinner fa-spin" />
                Loading map…
              </div>
            )}

            {/* Fit-all button */}
            <button
              type="button"
              onClick={() => setFitTrigger((k) => k + 1)}
              title="Zoom to show all markers"
              aria-label="Zoom to show all markers"
              style={{
                position: "absolute",
                right: 20,
                top: 40,
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "none",
                outline: "none",
                background: "#fff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 1000,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.25)")}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.2)")}
            >
              <i className="fas fa-expand-arrows-alt" style={{ fontSize: 18, color: "#374151" }} />
            </button>

            {/* Legend */}
            <div
              style={{
                position: "absolute",
                bottom: 24,
                left: 12,
                zIndex: 1000,
                background: "rgba(255,255,255,0.93)",
                backdropFilter: "blur(4px)",
                borderRadius: 10,
                padding: "8px 12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {[
                { label: "Pending action", color: "#ef4444" },
                { label: "Ongoing action", color: "#eab308" },
                { label: "Resolved", color: "#22c55e" },
              ].map(({ label, color }) => (
                <div
                  key={label}
                  style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, fontWeight: 600, color: "#374151" }}
                >
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* ── Sidebar ── */}
          <aside
            ref={sidebarRef}
            style={{
              width: SIDEBAR_W,
              minWidth: SIDEBAR_W,
              maxWidth: SIDEBAR_W,
              height: `calc(100vh - ${NAVBAR_H}px)`,
              overflowY: "auto",
              background: "#fff",
              borderLeft: "1px solid #e6e6e6",
              boxShadow: "0 0 24px rgba(0,0,0,0.05)",
              position: "relative",
            }}
          >
            {/* Sticky header */}
            <div
              style={{
                padding: "14px 16px 10px",
                borderBottom: "1px solid #f3f4f6",
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 10,
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <h5
                  style={{
                    marginBottom: 2,
                    fontWeight: 800,
                    fontSize: 15,
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {loading
                    ? "Loading…"
                    : `${filteredPosts.length} Bleep${filteredPosts.length !== 1 ? "s" : ""}`}
                  {loading && (
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: 13, color: "#64748b" }} />
                  )}
                </h5>
                {!loading && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    {posts.length} on map
                    {filterStatus !== "all" || filterCategory !== "all"
                      ? ` · ${filteredPosts.length} shown`
                      : ""}
                  </div>
                )}
                {err && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>{err}</div>}
              </div>

              {/* Filters */}
              <div style={{ display: "flex", gap: 7 }}>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    flex: 1,
                    fontSize: 12,
                    padding: "5px 8px",
                    borderRadius: 999,
                    border: "1px solid #d1d5db",
                    backgroundColor: "#f9fafb",
                    color: "#0f172a",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All statuses</option>
                  <option value="Pending action">Pending action</option>
                  <option value="Ongoing action">Ongoing action</option>
                  <option value="Resolved">Resolved</option>
                </select>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  style={{
                    flex: 1,
                    fontSize: 12,
                    padding: "5px 8px",
                    borderRadius: 999,
                    border: "1px solid #d1d5db",
                    backgroundColor: "#f9fafb",
                    color: "#0f172a",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Post cards */}
            <div style={{ padding: 12 }}>
              {!loading && filteredPosts.length === 0 && (
                <div style={{ padding: "48px 0", textAlign: "center", color: "#9ca3af" }}>
                  <i
                    className="fas fa-map-marker-alt"
                    style={{ fontSize: 30, display: "block", marginBottom: 10 }}
                  />
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    No bleeps match your filters.
                  </div>
                </div>
              )}

              {filteredPosts.map((post) => {
                const chip = statusChip(post.status);
                const color = statusColor(post.status);
                const isSelected = selectedPost?._id === post._id;

                return (
                  <div
                    id={`bleep-card-${post._id}`}
                    key={post._id}
                    onClick={() => handleSelect(post)}
                    style={{
                      marginBottom: 10,
                      borderRadius: 14,
                      overflow: "hidden",
                      cursor: "pointer",
                      border: isSelected ? `2px solid ${color}` : "1px solid #eef2f7",
                      boxShadow: isSelected
                        ? `0 0 0 3px ${color}22, 0 4px 14px rgba(2,6,23,0.08)`
                        : "0 4px 14px rgba(2,6,23,0.07)",
                      background: "#fff",
                      transition: "border-color 120ms, box-shadow 120ms",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.boxShadow = "0 8px 22px rgba(2,6,23,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.boxShadow = "0 4px 14px rgba(2,6,23,0.07)";
                    }}
                  >
                    {/* Status colour bar */}
                    <div style={{ height: 4, background: color }} />

                    <div style={{ padding: "10px 12px" }}>
                      {/* Chips */}
                      <div style={{ display: "flex", gap: 6, marginBottom: 7, flexWrap: "wrap" }}>
                        <span
                          style={{
                            padding: "3px 9px",
                            borderRadius: 999,
                            background: chip.bg,
                            color: chip.text,
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          {chip.label}
                        </span>
                        {post.category && (
                          <span
                            style={{
                              padding: "3px 9px",
                              borderRadius: 999,
                              background: "#f1f5f9",
                              color: "#475569",
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          >
                            {post.category}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#0f172a",
                          marginBottom: 7,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.45,
                        }}
                      >
                        {post.description}
                      </div>

                      {/* Location + date */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{ fontSize: 11, color: "#64748b", display: "flex", alignItems: "center", gap: 5 }}
                        >
                          <i className="fas fa-map-marker-alt" style={{ color: "#f59e0b", fontSize: 10 }} />
                          <span
                            style={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              maxWidth: 160,
                            }}
                          >
                            {post.location?.name || post.city || "—"}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
                          {new Date(post.dateTime).toLocaleDateString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      </div>

      {/* ── Detail modal ── */}
      <Modal show={!!selectedPost} onHide={() => setSelectedPost(null)} centered size="md">
        {selectedPost &&
          (() => {
            const chip = statusChip(selectedPost.status);
            const color = statusColor(selectedPost.status);
            return (
              <>
                <Modal.Header
                  closeButton
                  style={{ borderBottom: "1px solid #f3f4f6", padding: "12px 20px" }}
                >
                  <button
                    onClick={() => copyId(selectedPost._id)}
                    title="Click to copy post ID"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <code style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", letterSpacing: 0.2 }}>
                      {selectedPost._id}
                    </code>
                    <i
                      className={idCopied ? "fas fa-check" : "fas fa-copy"}
                      style={{ fontSize: 12, color: idCopied ? "#22c55e" : "#94a3b8" }}
                    />
                  </button>
                </Modal.Header>

                <Modal.Body style={{ padding: 20 }}>
                  {/* Chips */}
                  <div style={{ display: "flex", gap: 7, marginBottom: 14, flexWrap: "wrap" }}>
                    <span
                      style={{
                        padding: "5px 12px",
                        borderRadius: 999,
                        background: chip.bg,
                        color: chip.text,
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      {chip.label}
                    </span>
                    {selectedPost.category && (
                      <span
                        style={{
                          padding: "5px 12px",
                          borderRadius: 999,
                          background: "#f1f5f9",
                          color: "#475569",
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {selectedPost.category}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <div
                    style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.65, marginBottom: 14 }}
                  >
                    {selectedPost.description}
                  </div>

                  {/* Mini map */}
                  {(() => {
                    const mLat = selectedPost.location.coordinates[1];
                    const mLng = selectedPost.location.coordinates[0];
                    return (
                      <div style={{ position: "relative", marginBottom: 14 }}>
                        <div
                          style={{
                            borderRadius: 12,
                            overflow: "hidden",
                            height: 240,
                            border: "1px solid #e6e9ee",
                          }}
                        >
                          <MapContainer
                            key={selectedPost._id}
                            center={[mLat, mLng]}
                            zoom={15}
                            scrollWheelZoom
                            attributionControl={false}
                            style={{ height: "100%", width: "100%" }}
                          >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <CircleMarker
                              center={[mLat, mLng]}
                              pathOptions={{ color: "#fff", fillColor: color, fillOpacity: 1, weight: 3 }}
                              radius={10}
                            />
                          </MapContainer>
                        </div>
                        {/* Map overlay actions */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 10,
                            left: 10,
                            zIndex: 1100,
                            display: "flex",
                            gap: 6,
                          }}
                        >
                          <button
                            onClick={() => copyCoords(mLat, mLng)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "6px 11px",
                              borderRadius: 999,
                              background: "rgba(255,255,255,0.93)",
                              border: "1px solid rgba(0,0,0,0.09)",
                              color: "#0f172a",
                              fontWeight: 700,
                              fontSize: 12,
                              cursor: "pointer",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                              backdropFilter: "blur(4px)",
                            }}
                          >
                            <i
                              className={coordsCopied ? "fas fa-check" : "fas fa-copy"}
                              style={{ color: coordsCopied ? "#22c55e" : "#64748b", fontSize: 11 }}
                            />
                            {coordsCopied ? "Copied!" : `${mLat.toFixed(5)}, ${mLng.toFixed(5)}`}
                          </button>
                          <a
                            href={`https://maps.google.com/?q=${mLat},${mLng}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "6px 11px",
                              borderRadius: 999,
                              background: "rgba(255,255,255,0.93)",
                              border: "1px solid rgba(0,0,0,0.09)",
                              color: "#0f172a",
                              fontWeight: 700,
                              fontSize: 12,
                              textDecoration: "none",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                              backdropFilter: "blur(4px)",
                            }}
                          >
                            <i className="fas fa-external-link-alt" style={{ color: "#64748b", fontSize: 11 }} />
                            Google Maps
                          </a>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Address block */}
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e6e9ee",
                      borderRadius: 10,
                      padding: "10px 14px",
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "#64748b",
                        marginBottom: 3,
                      }}
                    >
                      Purchase Address
                    </div>
                    <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                      {selectedPost.address}
                    </div>
                    {selectedPost.location?.name && (
                      <>
                        <div style={{ height: 1, background: "#e6e9ee", margin: "8px 0" }} />
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            color: "#64748b",
                            marginBottom: 3,
                          }}
                        >
                          Pinned Location
                        </div>
                        <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                          {selectedPost.location.name}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Date */}
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <i className="far fa-clock" />
                    {formatDate(selectedPost.dateTime)}
                  </div>

                  {/* CTA */}
                  <a
                    href={`/post/${selectedPost._id}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 18px",
                      borderRadius: 999,
                      background: "#ff5100",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 13,
                      textDecoration: "none",
                    }}
                  >
                    View Full Post <i className="fas fa-arrow-right" />
                  </a>
                </Modal.Body>
              </>
            );
          })()}
      </Modal>
    </>
  );
};

export default BleepMap;
